import { useState, useEffect } from "react";
import {
  getStrategyWeights,
  updateStrategyWeight,
} from "../../services/adminService";
import type { StrategyWeightItem } from "../../services/adminService";

interface DiversifierWeightsPanelProps {
  className?: string;
}

export function DiversifierWeightsPanel({ className = "" }: DiversifierWeightsPanelProps) {
  const [strategies, setStrategies] = useState<StrategyWeightItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        setIsLoading(true);
        const data = await getStrategyWeights();
        setStrategies(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load weights");
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, []);

  const handleWeightChange = async (id: string, weight: number) => {
    try {
      setSavingId(id);
      setError(null);
      setSuccess(null);
      await updateStrategyWeight(id, weight);
      setStrategies((prev) =>
        prev.map((s) => (s.id === id ? { ...s, default_weight: weight } : s)),
      );
      setSuccess("Weight updated");
      setTimeout(() => setSuccess(null), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update weight");
    } finally {
      setSavingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className={`p-4 rounded-xl bg-surface-800/50 border border-surface-700/50 ${className}`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <div className="animate-spin w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full" />
          </div>
          <span className="text-surface-400">Loading strategy weights...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-xl bg-surface-800/50 border border-surface-700/50 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <svg className="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
            </svg>
          </div>
          <div>
            <div className="text-sm font-medium text-surface-100">Diversifier Default Weights</div>
            <div className="text-xs text-surface-400">
              Set the default weight multiplier (0–5) users see on the Diversifier page
            </div>
          </div>
        </div>
        {success && (
          <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded">{success}</span>
        )}
      </div>

      {error && (
        <div className="mb-3 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="space-y-2">
        {strategies.map((s) => (
          <div
            key={s.id}
            className="flex items-center justify-between p-3 rounded-lg bg-surface-900/50 border border-surface-700/30"
          >
            <div className="min-w-0 flex-1 mr-4">
              <div className="text-sm font-medium text-surface-200 truncate">{s.name}</div>
              <div className="text-xs text-surface-500 truncate">{s.slug}</div>
            </div>
            <div className="flex items-center gap-1.5">
              {savingId === s.id && (
                <div className="animate-spin w-3.5 h-3.5 border-2 border-primary-400 border-t-transparent rounded-full mr-1" />
              )}
              <div className="inline-flex items-center gap-0.5 bg-surface-800 rounded-lg p-0.5">
                {[0, 1, 2, 3, 4, 5].map((w) => (
                  <button
                    key={w}
                    type="button"
                    disabled={savingId === s.id}
                    onClick={() => handleWeightChange(s.id, w)}
                    className={`w-7 h-7 rounded-md text-xs font-semibold transition-all duration-150 ${
                      w === s.default_weight
                        ? w === 0
                          ? "bg-surface-600 text-surface-300 shadow-sm"
                          : "bg-primary-500 text-white shadow-sm"
                        : "text-surface-400 hover:text-surface-200 hover:bg-surface-700/50"
                    } ${savingId === s.id ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {w}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {strategies.length === 0 && (
        <div className="text-center py-4 text-sm text-surface-500">
          No featured community strategies found
        </div>
      )}
    </div>
  );
}
